import { useState, useEffect } from 'react';
import { api, type MarketDataStatusResponse } from '../../api/client';
import './MarketDataRefreshPanel.css';

interface RefreshState {
  source: string;
  loading: boolean;
  message: string | null;
  error: boolean;
}

type SourceKey = 'rentcast' | 'zumper' | 'zillow';

const SOURCE_CONFIG: Record<SourceKey, { label: string; description: string }> = {
  rentcast: {
    label: 'RentCast',
    description: 'ZIP-level rental data (1-8BR)',
  },
  zumper: {
    label: 'Zumper',
    description: 'City-level rents + demographics',
  },
  zillow: {
    label: 'Zillow',
    description: 'Metro-level rental data',
  },
};

export function MarketDataRefreshPanel() {
  const [status, setStatus] = useState<MarketDataStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshStates, setRefreshStates] = useState<Record<string, RefreshState>>({});

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getMarketDataStatus();
      setStatus(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (source: SourceKey) => {
    setRefreshStates(prev => ({
      ...prev,
      [source]: { source, loading: true, message: null, error: false }
    }));

    try {
      let result;
      if (source === 'rentcast') {
        result = await api.refreshRentCast({});
      } else if (source === 'zumper') {
        result = await api.refreshZumper({});
      } else {
        throw new Error('Zillow refresh not yet implemented');
      }

      // Handle different response formats
      const count = result.zipsRefreshed ?? result.marketsRefreshed ?? 0;
      const label = source === 'rentcast' ? 'ZIPs' : 'markets';

      setRefreshStates(prev => ({
        ...prev,
        [source]: {
          source,
          loading: false,
          message: `Refreshed ${count} ${label}`,
          error: !result.success
        }
      }));

      // Reload status after refresh
      setTimeout(loadStatus, 1000);
    } catch (err) {
      setRefreshStates(prev => ({
        ...prev,
        [source]: {
          source,
          loading: false,
          message: err instanceof Error ? err.message : 'Refresh failed',
          error: true
        }
      }));
    }
  };

  const handleRefreshAll = async () => {
    for (const source of ['rentcast', 'zumper'] as const) {
      await handleRefresh(source);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    // Check if it's just a date (YYYY-MM-DD) or full timestamp
    if (dateStr.length === 10) {
      return dateStr;
    }
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isStale = (lastUpdated: string | null) => {
    if (!lastUpdated) return true;
    const date = new Date(lastUpdated);
    const now = new Date();
    const daysDiff = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff > 7; // Stale if more than 7 days old
  };

  if (loading) {
    return <div className="market-data-panel loading">Loading market data status...</div>;
  }

  const anyRefreshing = Object.values(refreshStates).some(s => s.loading);
  const sources: SourceKey[] = ['rentcast', 'zumper', 'zillow'];

  return (
    <div className="market-data-panel">
      <div className="panel-header">
        <div>
          <h3>Market Data Sources</h3>
          <p className="panel-subtitle">External data for rental analysis</p>
        </div>
        <button
          className="btn-refresh-all"
          onClick={handleRefreshAll}
          disabled={anyRefreshing}
        >
          {anyRefreshing ? 'Refreshing...' : 'Refresh All'}
        </button>
      </div>

      {error && <div className="panel-error">{error}</div>}

      <div className="sources-grid">
        {sources.map((sourceKey) => {
          const config = SOURCE_CONFIG[sourceKey];
          const r2Status = status?.r2Sources?.[sourceKey];
          const lastUpdated = r2Status?.lastUpdated || null;
          const available = r2Status?.available || false;
          const stale = isStale(lastUpdated);
          const state = refreshStates[sourceKey];

          return (
            <div
              key={sourceKey}
              className={`source-card ${stale ? 'stale' : 'fresh'}`}
            >
              <div className="source-header">
                <span className="source-name">{config.label}</span>
                <span className={`source-badge ${stale ? 'badge-stale' : 'badge-fresh'}`}>
                  {!available ? 'No Data' : stale ? 'Stale' : 'Fresh'}
                </span>
              </div>

              <p className="source-desc">{config.description}</p>

              <div className="source-stats">
                <div className="stat">
                  <span className="stat-label">R2 Archive</span>
                  <span className="stat-value">{formatDate(lastUpdated)}</span>
                </div>
                {status?.database && sourceKey === 'rentcast' && (
                  <div className="stat">
                    <span className="stat-label">DB Records</span>
                    <span className="stat-value">{status.database.zipCount.toLocaleString()}</span>
                  </div>
                )}
              </div>

              {state?.message && (
                <div className={`source-message ${state.error ? 'error' : 'success'}`}>
                  {state.message}
                </div>
              )}

              <button
                className="btn-refresh-source"
                onClick={() => handleRefresh(sourceKey)}
                disabled={state?.loading || anyRefreshing || sourceKey === 'zillow'}
                title={sourceKey === 'zillow' ? 'Zillow refresh not yet implemented' : undefined}
              >
                {state?.loading ? 'Refreshing...' : `Refresh ${config.label}`}
              </button>
            </div>
          );
        })}
      </div>

      <p className="panel-footer">
        Data is stored in R2 for archival, then parsed into PostgreSQL for querying.
        {status?.checkedAt && ` Last checked: ${formatDate(status.checkedAt)}`}
      </p>
    </div>
  );
}
